import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, PackageCheck, Shirt, Undo2 } from 'lucide-react';
import { fetchEquipements, rendreEquipement } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const FILTRES = [
  { code: '', label: 'Tout' },
  { code: 'attendus', label: 'Attendus en retour' },
  { code: 'retard', label: 'En retard' },
  { code: 'rendus', label: 'Rendus' },
];

const TONS_STATUT: Record<string, string> = {
  fourni: 'bg-blue-100 text-blue-800',
  prete: 'bg-amber-100 text-amber-800',
  vendu: 'bg-green-100 text-green-800',
  autre: 'bg-gray-100 text-gray-600',
};

/**
 * L'inventaire des équipements du club.
 *
 * Un club de village sait qu'il lui manque trois maillots en fin de saison ;
 * il ne sait plus lesquels ni chez qui. Cet écran répond aux deux questions,
 * et la synthèse par taille est celle qu'on emporte chez le fournisseur —
 * un total ne sert à rien pour commander.
 */
export default function InventaireEquipements() {
  const { aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const peutGerer = aLeDroit('gerer_adherents');

  const [etat, setEtat] = useState('');
  const [type, setType] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['equipements', etat, type],
    queryFn: () => fetchEquipements({ etat, type }),
  });

  const rendre = useMutation({
    mutationFn: ({ id, etatRetour }: { id: number; etatRetour: string }) =>
      rendreEquipement(id, { etatRetour }),
    onSuccess: (d: any) => {
      setNotice(d.message);
      queryClient.invalidateQueries({ queryKey: ['equipements'] });
      queryClient.invalidateQueries({ queryKey: ['adherent'] });
      setTimeout(() => setNotice(null), 3000);
    },
  });

  if (query.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const equipements: any[] = query.data?.equipements ?? [];
  const synthese: any[] = query.data?.synthese ?? [];
  const catalogue: any[] = query.data?.configuration?.equipements ?? [];
  const etats: Record<string, string> = query.data?.vocabulaire?.etats ?? {};

  const attendus = synthese.reduce((total, l) => total + l.attendus, 0);
  const retards = synthese.reduce((total, l) => total + l.enRetard, 0);
  const sortis = synthese.reduce((total, l) => total + l.sortis, 0);

  return (
    <div className="space-y-4">
      {notice && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-2 text-sm text-green-800">
          {notice}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <Carte libelle="Sortis" valeur={sortis} />
        <Carte libelle="Attendus en retour" valeur={attendus} ton={attendus > 0 ? 'ambre' : 'neutre'} />
        <Carte libelle="En retard" valeur={retards} ton={retards > 0 ? 'rouge' : 'neutre'} />
      </div>

      {/* La synthèse par taille : celle qu'on emporte chez le fournisseur. */}
      {synthese.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Ce qui est sorti, par taille</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-4 font-medium">Équipement</th>
                  <th className="py-2 pr-4 font-medium">Taille</th>
                  <th className="py-2 pr-4 font-medium text-right">Sortis</th>
                  <th className="py-2 pr-4 font-medium text-right">Attendus</th>
                  <th className="py-2 font-medium text-right">En retard</th>
                </tr>
              </thead>
              <tbody>
                {synthese.map((l, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-1.5 pr-4 text-gray-800">{l.libelle}</td>
                    <td className="py-1.5 pr-4 text-gray-600">{l.taille || '—'}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums text-gray-700">{l.sortis}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums text-amber-700">
                      {l.attendus || <span className="text-gray-300">0</span>}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-red-700">
                      {l.enRetard || <span className="text-gray-300">0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1">
          {FILTRES.map((f) => (
            <button
              key={f.code}
              onClick={() => setEtat(f.code)}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                etat === f.code ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {catalogue.length > 0 && (
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
          >
            <option value="">Tous les équipements</option>
            {catalogue.map((c) => (
              <option key={c.code} value={c.code}>
                {c.libelle}
              </option>
            ))}
          </select>
        )}
        <span className="text-xs text-gray-500 ml-auto">{equipements.length} ligne(s)</span>
      </div>

      {equipements.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
          <Shirt className="w-8 h-8 mx-auto text-gray-300 mb-3" />
          Rien n'est sorti pour l'instant. Les équipements se remettent depuis la fiche d'un adhérent.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="px-4 py-3 font-medium">Adhérent</th>
                <th className="px-4 py-3 font-medium">Équipement</th>
                <th className="px-4 py-3 font-medium">Taille</th>
                <th className="px-4 py-3 font-medium">N°</th>
                <th className="px-4 py-3 font-medium">À quel titre</th>
                <th className="px-4 py-3 font-medium">Remis le</th>
                <th className="px-4 py-3 font-medium">Retour</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {equipements.map((e) => (
                <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900">{e.adherent}</td>
                  <td className="px-4 py-2.5 text-gray-700">
                    {e.libelle}
                    {e.quantite > 1 && <span className="text-gray-400"> ×{e.quantite}</span>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{e.taille || '—'}</td>
                  <td className="px-4 py-2.5 tabular-nums text-gray-600">{e.numero || '—'}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs ${
                        TONS_STATUT[e.statut] ?? 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {e.statutLibelle}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{e.remisLe}</td>
                  <td className="px-4 py-2.5">
                    {e.renduLe ? (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <PackageCheck className="w-3.5 h-3.5" />
                        {e.renduLe}
                        {e.etatRetour && (
                          <span className="text-gray-400"> ({etats[e.etatRetour] ?? e.etatRetour})</span>
                        )}
                      </span>
                    ) : e.attendu ? (
                      <span className={e.enRetard ? 'inline-flex items-center gap-1 text-red-700' : 'text-gray-600'}>
                        {e.enRetard && <AlertTriangle className="w-3.5 h-3.5" />}
                        {e.rendreAvant ? `avant le ${e.rendreAvant}` : 'attendu'}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {peutGerer && e.attendu && (
                      <RendreBouton
                        etats={etats}
                        onRendre={(etatRetour) => rendre.mutate({ id: e.id, etatRetour })}
                        enCours={rendre.isPending}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Rendre un prêt demande l'état constaté : c'est lui qui décide du rachat. */
function RendreBouton({
  etats,
  onRendre,
  enCours,
}: {
  etats: Record<string, string>;
  onRendre: (etat: string) => void;
  enCours: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);

  if (!ouvert) {
    return (
      <button
        onClick={() => setOuvert(true)}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
      >
        <Undo2 className="w-3.5 h-3.5" />
        Rendu
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1">
      <select
        autoFocus
        defaultValue=""
        onChange={(e) => {
          if (e.target.value) {
            onRendre(e.target.value);
            setOuvert(false);
          }
        }}
        disabled={enCours}
        className="px-2 py-1 rounded-lg border border-gray-300 text-xs"
      >
        <option value="">Dans quel état ?</option>
        {Object.entries(etats).map(([code, label]) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </select>
      <button onClick={() => setOuvert(false)} className="text-xs text-gray-400 hover:text-gray-600">
        Annuler
      </button>
    </div>
  );
}

function Carte({ libelle, valeur, ton = 'neutre' }: { libelle: string; valeur: number; ton?: string }) {
  const couleurs: Record<string, string> = {
    neutre: 'text-gray-900',
    ambre: 'text-amber-700',
    rouge: 'text-red-700',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-gray-400">{libelle}</p>
      <p className={`text-xl font-semibold tabular-nums ${couleurs[ton]}`}>{valeur}</p>
    </div>
  );
}
